import { Component, ElementRef, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { takeUntil } from 'rxjs';
import { AnalyticsService } from '../../../@core/utils';
import { ROLE, ROLES, ROLEVALUES } from '../../../enum/roles.enum';
import { ToastStatus, ToasterMessages } from '../../../enum/toaster-messages.enum';
import { ClearObservableHelper } from '../../../helpers/ClearObservableHelper';
import { SchoolCalendar } from '../../../interface/calendar';
import { School } from '../../../interface/school';
import { AcademicYearService } from '../../../services/academic-year.service';
import { AuthService } from '../../../services/auth.service';
import { CalendarService } from '../../../services/calendar.service';
import { SchoolService } from '../../../services/school.service';
import { StorageService } from '../../../services/storage.service';
import { ToasterService } from '../../../services/toaster.service';
import { MatDialogComponent } from '../../../shared/mat-dialog/mat-dialog.component';

@Component({
  selector: 'ngx-select-header-school',
  templateUrl: './select-header-school.component.html',
  styleUrls: ['./select-header-school.component.scss'],
})
export class SelectHeaderSchoolComponent extends ClearObservableHelper implements OnInit {
  user: any;
  isDropdownOpen = false;
  selectedSchool: School;
  schools: any = [];
  public searchText: string = '';
  currentSchoolCalendars: SchoolCalendar[];
  selectedSchoolCalendar: SchoolCalendar;
  isAcademicYearDropdownOpen = false;
  userRoles = ROLES;
  onBoardSchool: SchoolCalendar[];
  currentTeachingInformation: any;
  isHeaderDisabled: boolean = false;
  isSchoolHeaderDisabled: boolean = false;
  @ViewChild('dropdownRef') dropdownRef!: ElementRef;
  @ViewChild('schoolDropdownRef') schoolDropdownRef!: ElementRef;
  @ViewChild('searchBox', { static: false }) searchBoxRef!: ElementRef;
  @ViewChild('highlightAcadmicYear') highlightAcadmicYear: TemplateRef<any>;
  public dialogRef: NbDialogRef<any>;

  constructor(
    public storageService: StorageService,
    private authService: AuthService,
    private schoolService: SchoolService,
    public analyticsService: AnalyticsService,
    private calendarService: CalendarService,
    public academicService: AcademicYearService,
    public matDialog: MatDialog,
    public router: Router,
    private toasterService: ToasterService,
    private dialogService: NbDialogService,
  ) {
    super();
    this.authService.updateValue.subscribe(async (value: string) => {
      if (value) {
        const user = await this.storageService.getItem('user');
        this.user = await this.authService.userById(user.user.id);
      }
    });

    this.authService.setSelectedSchool.subscribe(async (x) => {
      if (x) {
        // Update selected school when notified
        this.showSelectedSchool();
        await this.getSchoolCalendar();
      }
    });
  }

  async ngOnInit() {
    try {
      this.user = await this.storageService.getItem('user');
      // Refresh schools list when other parts of the app signal that school data changed
      this.schoolService.newSchool$.pipe(takeUntil(this.destroy$)).subscribe(async (shouldRefresh) => {
        try {
          if (shouldRefresh) {
            await this.getSchools();
          }
        } catch (err) {
          console.log('SelectHeaderSchoolComponent ~ ngOnInit ~ err:', err);
        }
      });
      this.academicService.newAcademicYear$.pipe(takeUntil(this.destroy$)).subscribe(async (isNewAcademicYear) => {
        // If a new academic year is added, refresh the school calendar dropdown list
        if (isNewAcademicYear) {
          await this.getSchoolCalendar();
        }
      });

      // Listen to disable academic year header changes
      this.academicService.disableAcademicHeader.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        this.isHeaderDisabled = value;
      });

      // Listen to disable school header changes
      this.academicService.disableSchoolHeader.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        this.isSchoolHeaderDisabled = value;
      });

      if (this.user) this.user = await this.authService.userById(this.user?.user.id);

      if (
        this.user?.profileData?.isViewMultipleSchools === true ||
        this.user?.role_id === ROLES.SUPER_ADMIN ||
        this.user?.role_id === ROLES.PARENT
      ) {
        await this.getSchools();
        this.showSelectedSchool();
      } else {
        if (this.user.role_id === ROLEVALUES.TEACHER_ONLY) {
          const currentSchool = this.user?.school;
          currentSchool['name'] = 'Teachers Only School';
          this.selectedSchool = currentSchool;
        } else {
          this.selectedSchool = this.user?.school;
        }
      }
      // Get school calendar
      await this.getSchoolCalendar();
    } catch (error) {
      console.log('SelectHeaderSchoolComponent ~ ngOnInit ~ error:', error);
    }
  }

  adminSchools() {
    try {
      const schoolsIds = this?.user?.profileData?.selectedSchools;
      schoolsIds.unshift(this?.user?.school_id);
      return schoolsIds;
    } catch (error) {
      // console.log(error)
    }
  }

  /**
   * Fetches the list of schools based on the user's role.
   * If the user is an admin (role 19), all schools are fetched. Otherwise, only schools
   * that the user has permission to view are fetched.
   *
   * @returns {void}
   */
  async getSchools() {
    try {
      this.schools =
        this.user?.role_id === ROLES.SUPER_ADMIN
          ? await this.schoolService.getAllSchools()
          : await this.schoolService.getMultipleSchoolsByIds({
              school_ids: this.adminSchools(),
            });
    } catch (error) {
      // console.log(error);
    }
  }

  /**
   * Displays the selected school based on user data or a previously stored selection.
   * @returns {void}
   */
  private showSelectedSchool(): void {
    try {
      const localStorageSchool = this.storageService.getItem('selectedSchoolByAdmin');

      this.selectedSchool = localStorageSchool?.id
        ? localStorageSchool
        : this.user?.role_id === ROLES.SUPER_ADMIN
          ? this.schools[0]
          : this.user?.school;
      if (!localStorageSchool) {
        this.storageService.setItem('selectedSchoolByAdmin', this.selectedSchool);
        this.storageService.setSelectedSchoolsToLocalStorage.emit(true);
      }
    } catch (error) {
      // console.log(error);
    }
  }

  /**
   * Toggles the visibility of the school selection dropdown.
   * @returns {void}
   */
  public showDropDown(): void {
    try {
      if (this.user?.profileData?.isViewMultipleSchools === true || this.user?.role_id === ROLES.SUPER_ADMIN) {
        // Check if admin is able to open the school dropdown or not
        if (this.shouldRestrictSchoolDropdown('schools')) {
          return;
        }
        // Use setTimeout to ensure the toggle happens after the current call stack is cleared
        setTimeout(() => {
          this.isDropdownOpen = !this.isDropdownOpen;
        }, 0);
        this.isAcademicYearDropdownOpen = false;
      }
    } catch (error) {
      console.log('SelectHeaderSchoolComponent ~ showDropDown ~ error:', error);
    }
  }

  /**
   * @description
   * Changes the current selected school and stores it in local storage.
   * This function updates the selected school, closes the school selection dropdown,
   * and refreshes the school calendar based on the selected school.
   * It is triggered when the user selects a different school from the dropdown.
   * @param {School} school The school to be selected.
   * @returns {void}
   */
  public async changeCurrentSchool(school: School): Promise<void> {
    this.isDropdownOpen = false;
    this.selectedSchool = school;
    this.storageService.setItem('selectedSchoolByAdmin', school);
    this.academicService.emitSchoolUpdateFromHeader(true);
    await this.getSchoolCalendar();
    this.storageService.setSelectedSchoolsToLocalStorage.emit(true);
  }

  /**
   * @returns void
   * @description This function is used to get school calendar by school id
   * @description This method fetches the calendar for the selected school. It checks the user's role
   * and whether they have access to view multiple schools, and fetches the corresponding calendar accordingly.
   * It assigns the fetched calendars to `currentSchoolCalendars` and selects the first calendar as `selectedSchoolCalendar`.
   */
  async getSchoolCalendar(): Promise<void> {
    try {
      // Get the currently selected school from local storage
      const currentSelectedSchool = this.storageService.getItem('selectedSchoolByAdmin') || null;
      const params = {
        school_id:
          this.user?.role_id === ROLES.SUPER_ADMIN
            ? this.selectedSchool?.id
            : this.user?.role_id === ROLES.PARENT
              ? currentSelectedSchool?.id
              : this.user?.profileData?.isViewMultipleSchools
                ? this.selectedSchool?.id
                : this.user?.school_id,
      };
      this.currentSchoolCalendars = await this.calendarService.schoolEventBySchoolId(params);

      // Keep only activated and unarchived calendars
      this.currentSchoolCalendars = this.currentSchoolCalendars.filter(
        (calendar) => calendar.isActivate && !calendar.isArchive,
      );

      // Optionally, set the first active calendar as selected
      // this.selectedSchoolCalendar = this.currentSchoolCalendars[0] || null;

      // Filter and store the academic year where flag marked as 'isFirst' for the onboarding school
      // This ensures the default academic year is shown to the user done while onboarding

      this.onBoardSchool = this.currentSchoolCalendars.filter((calendar) => calendar.isFirst);

      const storedCalendar = this.storageService.getItem('selectedSchoolCalendarByAdmin');
      const validStoredCalendar = this.currentSchoolCalendars.find((c) => c.id === storedCalendar?.id);
      // If found, use it; otherwise, fall back to the first calendar in the list
      this.selectedSchoolCalendar = validStoredCalendar || this.onBoardSchool[0];
      // Set the calendar as 0 instead of undefined/null
      this.storageService.setItem('selectedSchoolCalendarByAdmin', this.selectedSchoolCalendar ?? 0);
    } catch (error) {
      console.log('getSchoolCalendar ~ error:', error);
    }
  }

  /**
   * Toggles the academic year dropdown visibility.
   *
   * @param {MouseEvent} event The mouse event triggering the toggle.
   * @returns {void}
   */
  toggleAcademicYearDropdown(event: MouseEvent): void {
    try {
      this.setNewAcademicYearStatusForUser();
      if (this.restrictAccessToChangeAcademicYear()) {
        return;
      }

      // Ensure that the teacher-only user is not able to change the academic year before updating the classrooms when onboarded to the real school.
      const userData = this.storageService.getItem('user');
      if (userData.user?.profileData?.isClassroomsSetup === false) {
        this.router.navigateByUrl('/test-quizzes');
        this.toasterService.showToast(
          'Please update all class schedules to match the bell schedule before continuing.',
          'danger',
        );

        return;
      }

      // Check if admin is able to open the calendar year dropdown or not
      if (this.shouldRestrictSchoolDropdown('academic years')) {
        return;
      }

      event.stopPropagation();
      if (this.currentSchoolCalendars && this.currentSchoolCalendars.length > 0) {
        this.isDropdownOpen = false;
        this.isAcademicYearDropdownOpen = !this.isAcademicYearDropdownOpen;
      }
    } catch (error) {
      console.log('toggleAcademicYearDropdown ~ error:', error);
    }
  }

  /**
   * @description
   * Restricts access to the next academic year for students who have an ongoing assessment.
   * If the user is a student and has an ongoing assessment, they will be prompted to finish it first.
   *
   * @returns {boolean}
   * Returns `true` if the user is a student with an ongoing assessment, otherwise returns `false`.
   */
  restrictAccessToChangeAcademicYear(): boolean {
    try {
      const ongoingAssessment = this.storageService.getItem('currentOnGoingAssessment');

      if (this.user.role_id === ROLES.STUDENT && ongoingAssessment) {
        this.toasterService.showToast(ToasterMessages.YEAR_CHANGED_DURING_ASSESSMENT_MESSAGE, ToastStatus.WARNING);
        return true;
      }

      // Check if academic year header is disabled
      if (this.isHeaderDisabled) {
        this.toasterService.showToast(ToasterMessages.ACADEMIC_YEAR_DISABLED_MESSAGE, ToastStatus.WARNING);
        return true;
      }

      return false;
    } catch (error) {
      console.log('restrictAccessToChangeAcademicYear ~ error:', error);
    }
  }

  /**
   * Changes the current selected school calendar and stores it in local storage.
   *
   * @param {any} calendar The calendar to be selected.
   * @returns {Promise<void>}
   */
  public async changeCurrentSchoolCalendar(calendar: any): Promise<void> {
    try {
      if (this.selectedSchoolCalendar.id === calendar.id) {
        return;
      }
      this.isAcademicYearDropdownOpen = false;
      this.selectedSchoolCalendar = calendar;
      this.storageService.setItem('selectedSchoolCalendarByAdmin', calendar);
      this.storageService.setSelectedSchoolsCalendarToLocalStorage.emit(calendar);
      await this.geAcademicInformation();
      // Ensure the user remains on the same page when the academic year changes
      // if (!this.router.url.includes('test-quizzes')) {
      await this.handleNewYearOnboarding(calendar);
      // }
    } catch (error) {
      console.log('changeCurrentSchoolCalendar ~ error:', error);
    }
  }

  //  New Year Onboarding for teachers and redirecting user to respective dashboard as per role
  async handleNewYearOnboarding(calendar: SchoolCalendar) {
    try {
      if (
        !calendar?.isFirst &&
        ((this.user.role_id >= ROLES.RSP_TEACHER && this.user.role_id <= ROLES.SPED_SUPPORT_STAFF) ||
          this.user.role_id === ROLES.TEACHER_ASSISTANT)
      ) {
        if (this.currentTeachingInformation && this.currentTeachingInformation.length === 0) {
          this.openSetupAcademicInfoModal();
          this.authService.disabledSideMenu.emit(true);
        } else {
          if (this.currentTeachingInformation[0]?.isActive === true && !this.router.url.includes('profile')) {
            this.authService.disabledSideMenu.emit(false);

            // Redirect user to respective dashboard as per role
            if (this.router.url.includes('teacher-management')) {
              this.redirectUserToDashboardAsPerRole();
            }
          } else {
            if (this.currentTeachingInformation[0]?.isActive === true) {
              this.authService.disabledSideMenu.emit(false);

              // this.redirectUserToDashboardAsPerRole();
            } else {
              this.openSetupAcademicInfoModal();
              this.authService.disabledSideMenu.emit(true);
            }
          }
        }
      } else {
        this.authService.disabledSideMenu.emit(false);
        // Redirect user to respective dashboard as per role
        if (this.router.url.includes('teacher-management')) {
          this.redirectUserToDashboardAsPerRole();
        }
      }
    } catch (error) {
      // console.log(error);
    }
  }

  // Redirect user to respective dashboard as per role
  redirectUserToDashboardAsPerRole() {
    const roleId = this.user?.role_id;

    if (!roleId) return;

    const rolePaths = {
      [ROLE.TEACHER]: '/test-quizzes/classroom-details/teacher-classroom/true',
      [ROLE.ADMIN]: '/dashboard/admin-dashboard',
      [ROLE.STUDENT]: '/dashboard/student-dashboard',
      [ROLE.PARENT]: '/dashboard/parent-dashboard',
      [ROLE.SUPER_ADMIN]: '/dashboard/admin-list',
      [ROLE.STAFF]: '/calendar',
    };

    // Iterate through the roles and check the user role
    for (const role in rolePaths) {
      if (this.storageService.checkUserRole(roleId, role)) {
        this.router.navigateByUrl(rolePaths[role]);
        return;
      }
    }
  }

  // Modal for setting up academic information for teachers if not set
  openSetupAcademicInfoModal(): void {
    const dialogRef = this.matDialog.open(MatDialogComponent, {
      width: '400px',
      data: {
        title: 'Welcome',
        icon: '/assets/images/portal-logo.png',
        accessCodeLabel: 'Welcome to your Respond Edu Portal',
        confirmationMessage:
          'please take a moment to set up your academic information. This is a one-time setup, so please provide the details carefully.',
        cancelButtonText: 'No',
        confirmButtonText: 'Ok',
        cancelTrackEvent: 'Cancel Confirmation',
        confirmTrackEvent: 'Confirmation',
        trackPage: 'Calendar',
      },
      // prevent user to close dialog box by clicking on the backdrop
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.navigateAndRender();
      }
    });
  }

  // Navigate user to teacher management page and render the component as per teaching information
  navigateAndRender() {
    try {
      if (this.currentTeachingInformation && this.currentTeachingInformation.length === 0) {
        this.router.navigateByUrl('/teacher-management');
      } else {
        this.router.navigateByUrl(`/teacher-management/teacher/${this.currentTeachingInformation?.[0].id}`);
      }
    } catch (error) {
      console.log(error);
    }
  }

  // Get academic information for the selected calendar and user
  async geAcademicInformation() {
    try {
      const calendar = this.storageService.getItem('selectedSchoolCalendarByAdmin');
      const user = this.storageService.getItem('user');
      const body = {
        user_id: user?.user?.id,
        school_id: user?.user?.school_id,
        school_calendar_id: calendar?.id,
      };
      this.currentTeachingInformation = await this.calendarService.academicInformationByCalendarId(body);
    } catch (error) {
      console.log('geAcademicInformation error', error);
    }
  }

  /**
   * @description Method is used to restrict dropdown when creating a school year
   * @param dropdownName - Name of the dropdown that user wants to open
   * @returns Boolean
   */
  private shouldRestrictSchoolDropdown(dropdownName: string): boolean {
    try {
      const url = this.router.url;

      // Show toaster message and restrict school dropdown
      if (this.isSchoolHeaderDisabled) {
        this.toasterService.showToast(ToasterMessages.SCHOOL_HEADER_DISABLED_MESSAGE, ToastStatus.WARNING);
        return true;
      }

      if (url.includes('manage-school-years') && !url.includes('list-view')) {
        this.toasterService.showToast(`You cannot switch ${dropdownName} while creating a new school year.`, 'danger');
        return true;
      }
      return false;
    } catch (error) {
      console.log('SelectHeaderSchoolComponent ~ shouldRestrictSchoolDropdown ~ error:', error);
      return false;
    }
  }

  // Listen to clicks anywhere outside the dropdown

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Check academic year dropdown
    // Skip closing the school dropdown if user clicks inside the search box
    if (this.searchBoxRef?.nativeElement.contains(event.target)) {
      return;
    }
    if (this.isAcademicYearDropdownOpen && this.dropdownRef) {
      const clickedInsideAcademic = this.dropdownRef.nativeElement.contains(event.target);
      if (!clickedInsideAcademic) {
        this.isAcademicYearDropdownOpen = false;
      }
    }

    // Check school dropdown
    if (this.isDropdownOpen && this.schoolDropdownRef) {
      const clickedInsideSchool = this.schoolDropdownRef.nativeElement.contains(event.target);
      if (!clickedInsideSchool) {
        this.isDropdownOpen = false;
      }
    }
  }

  /**
   * @description Updates the user's profile to set the `newAcademicYearAdded` flag to false, indicating that the new academic year has been processed or acknowledged.
   * The method sends a request to update the user data, resetting the flag.
   *
   * @return {Promise<void>} A promise that resolves when the update operation is complete.
   */
  async setNewAcademicYearStatusForUser() {
    try {
      if (
        this.user?.profileData?.hasOwnProperty('newAcademicYearAdded') &&
        this.user?.profileData?.newAcademicYearAdded === true
      ) {
        const userBody = {
          ...this.user,
          profileData: {
            ...this.user?.profileData,
            newAcademicYearAdded: false,
          },
        };
        this.user = await this.authService.updateUser(this.user?.id, userBody, false);
      }
    } catch (error) {
      console.error('Error updating user with new academic year status:', error);
    }
  }
}
